import { useVerify2FaGoogleMutation } from "../../../api/mutations/useVerify2FaGoogleMutation";
import { useVerify2FaOtpMutation } from "../../../api/mutations/useVerify2FaOtpMutation";
import { TWO_FACTOR_TYPES } from "../../../consts/api";
import { useAppTranslation } from "../../../i18n/useAppTranslation";
import { uiNotification } from "../../../services/UINotificationService";
import { extractApiError } from "../../../utilities/api";
import { authService } from "../../../services/AuthService";
import { ROUTES } from "../../../consts/routes";
import { useSwitchWorkspace } from "../../../hooks/useSwitchWorkspace";
import { trustedDeviceToken } from "../../../utilities/localStorage";

export function useTwoFactorLogin() {
  const wpuId = authService.getPreLoginData().wpuId;
  const preLoginData = authService.getPreLoginData();

  const { tAuth } = useAppTranslation.Auth();
  const { switchWorkspace, isWorkspaceSwitching } = useSwitchWorkspace();

  const { mutateAsync: mutateOtp, isLoading: isOtpLoading } =
    useVerify2FaOtpMutation();

  const { mutateAsync: mutateGoogle, isLoading: isGoogleLoading } =
    useVerify2FaGoogleMutation();

  const verifyOtp = async (code, twoFactorType, trust = false) => {
    const { accessToken, workspaces, trustedDeviceToken, userHash } =
      await mutateOtp({
        wpuId,
        otp: code,
        trustDevice: trust,
        method: twoFactorType,
      });
    return {
      accessToken,
      workspaces,
      trustedDeviceToken,
      userHash,
    };
  };

  const verifyGoogle = async (code, trust = false) => {
    const { accessToken, workspaces, trustedDeviceToken, userHash } =
      await mutateGoogle({
        wpuId,
        googleAuthCode: preLoginData.googleAuthCode,
        authToken: code,
        trustDevice: trust,
      });
    return {
      accessToken,
      workspaces,
      trustedDeviceToken,
      userHash,
    };
  };

  const verify = async (formValues, twoFactorType, { resetForm } = {}) => {
    try {
      if (!wpuId) throw new Error();

      const isOpt =
        twoFactorType === TWO_FACTOR_TYPES.email ||
        twoFactorType === TWO_FACTOR_TYPES.sms;

      const isGoggle =
        twoFactorType === TWO_FACTOR_TYPES.google &&
        Boolean(preLoginData.googleAuthCode);

      let res = {
        accessToken: null,
        workspaces: [],
        trustedDeviceToken: null,
        userHash: null,
      };

      if (isOpt) {
        res = await verifyOtp(
          formValues.code,
          twoFactorType,
          formValues.trustDevice,
        );
      } else if (isGoggle) {
        res = await verifyGoogle(formValues.code, formValues.trustDevice);
      } else {
        throw new Error();
      }

      authService.setLoginData({ accessToken: res.accessToken, wpuId });

      if (res.trustedDeviceToken) {
        trustedDeviceToken.set({
          user: res.userHash,
          token: res.trustedDeviceToken,
        });
      }

      if (res.workspaces.length === 1) {
        switchWorkspace({ workspaceId: res.workspaces[0].id }).then(
          authService.afterLogin,
        );
      } else {
        window.location.href = ROUTES.workspaces();
      }
    } catch (e) {
      const key = extractApiError(e);
      uiNotification.error(
        tAuth([`apiError.${key}`, "error.fallback2StepLogin"]),
      );
      resetForm?.();
      return Promise.reject(e);
    }
  };

  return {
    verify,
    isVerifying: isOtpLoading || isGoogleLoading || isWorkspaceSwitching,
  };
}
